import React, { useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
import { LOCALES, type LocaleId } from '../../i18n/locales';
import { SettingsPath } from './SettingsChrome';
import { SETTINGS_HOME } from './settings-paths';
import './settings-home.css';

const LanguageSettings: React.FC = () => {
  const { t, locale, setLocale } = useLanguage();
  const [query, setQuery] = useState('');

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return LOCALES;
    return LOCALES.filter((item) =>
      `${item.nativeName} ${item.englishName} ${item.id}`.toLowerCase().includes(needle)
    );
  }, [query]);

  const choose = (id: LocaleId) => {
    setLocale(id);
  };

  return (
    <div className="set">
      <SettingsPath
        items={[
          { label: t('settings.back'), to: SETTINGS_HOME },
          { label: t('settings.home.language.title') },
        ]}
      />
      <p className="set-lead">{t('language.lead')}</p>
      <div className="set-preview">
        <span>{t('language.previewLabel')}</span>
        <strong>{t('language.preview')}</strong>
      </div>
      <label className="set-search">
        <Search size={16} aria-hidden="true" />
        <span className="visually-hidden">{t('language.searchLabel')}</span>
        <input
          type="search"
          value={query}
          placeholder={t('language.searchPlaceholder')}
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>
      {visible.length === 0 ? (
        <p className="set-empty-search">{t('language.empty')}</p>
      ) : (
        <div className="set-langs" role="radiogroup" aria-label={t('language.listLabel')}>
          {visible.map((item) => {
            const selected = item.id === locale;
            return (
              <button
                key={item.id}
                type="button"
                className="set-lang"
                role="radio"
                aria-checked={selected}
                onClick={() => choose(item.id)}
              >
                <span className="set-lang__mark" aria-hidden="true">
                  {item.mark}
                </span>
                <span className="set-lang__copy">
                  <strong>{item.nativeName}</strong>
                  {item.nativeName !== item.englishName ? <small>{item.englishName}</small> : null}
                </span>
                {item.id === 'en-US' ? <span className="set-badge">{t('language.defaultBadge')}</span> : null}
                {selected ? <Check className="set-lang__check" size={18} aria-hidden="true" /> : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSettings;
